import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';
import { useTenantAuth } from '../context/TenantAuthContext';

const STATUSES = ['active', 'inactive'];

export default function TenantBillingProfilePage() {
  const { hasPermission } = useTenantAuth();
  const canManage = hasPermission('tenant.billing.manage');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [vat, setVat] = useState('');
  const [invRef, setInvRef] = useState('');
  const [status, setStatus] = useState('active');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch('/api/tenant/billing/profile');
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const p = json.profile;
      if (p) {
        setName(p.billing_contact_name || '');
        setEmail(p.billing_contact_email || '');
        setPhone(p.billing_contact_phone || '');
        setAddress(p.billing_address || '');
        setVat(p.vat_number || '');
        setInvRef(p.invoice_reference || '');
        setStatus(p.status || 'active');
      } else {
        setName('');
        setEmail('');
        setPhone('');
        setAddress('');
        setVat('');
        setInvRef('');
        setStatus('active');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const res = await tenantApiFetch('/api/tenant/billing/profile', {
        method: 'PUT',
        body: {
          billing_contact_name: name.trim(),
          billing_contact_email: email.trim(),
          billing_contact_phone: phone.trim() || null,
          billing_address: address.trim() || null,
          vat_number: vat.trim() || null,
          invoice_reference: invRef.trim() || null,
          status
        }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
      setSuccess(true);
      if (json.profile) {
        setName(json.profile.billing_contact_name || '');
        setEmail(json.profile.billing_contact_email || '');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Official billing contact and purchase order reference for your organization. Invoices issued by the platform
        appear under the Invoices tab.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Saved.
        </Alert>
      )}
      <Paper sx={{ p: 2, maxWidth: 720 }}>
        <Box component="form" onSubmit={save}>
          <TextField
            fullWidth
            required
            label="Billing contact name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            disabled={!canManage}
          />
          <TextField
            fullWidth
            required
            type="email"
            label="Billing email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            disabled={!canManage}
          />
          <TextField
            fullWidth
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            margin="normal"
            disabled={!canManage}
          />
          <TextField
            fullWidth
            label="Billing address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            margin="normal"
            multiline
            minRows={3}
            disabled={!canManage}
          />
          <TextField
            fullWidth
            label="VAT / tax ID"
            value={vat}
            onChange={(e) => setVat(e.target.value)}
            margin="normal"
            disabled={!canManage}
          />
          <TextField
            fullWidth
            label="Default invoice / PO reference"
            value={invRef}
            onChange={(e) => setInvRef(e.target.value)}
            margin="normal"
            helperText="Shown on invoices where applicable"
            disabled={!canManage}
          />
          <FormControl fullWidth margin="normal" disabled={!canManage}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TenantCan permission="tenant.billing.manage">
            <Box sx={{ mt: 2 }}>
              <Button type="submit" variant="contained" disabled={saving || !name.trim() || !email.trim()}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </TenantCan>
        </Box>
      </Paper>
    </Box>
  );
}
